import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import TopBar from '../AppShell/TopBar';
import ThemeToggle from '../AppShell/ThemeToggle';
import {
    Button, IconButton, SegmentedControl, CloseIcon,
    KpiBand, KpiSection, StackBar, Buckets, Bucket,
    Chip, GapChip,
    PillList,
    Tooltip,
    Popover,
    Drawer,
    Dialog, ConfirmDialog,
    ToastRegion, useToast,
    Tabs,
    DataTable,
    EmptyState, ErrorState, LoadingState, Skeleton,
} from '../ui';
import { formatCurrency } from '../../utils/formatting';
import { isV1Enabled } from '../../config/uiVersion';
import { formatMoneyShort, LOT_VALUE_TIPS } from '../../utils/lotValue';
import { describeCacheAge } from '../../utils/cacheAge';
import { AI_PRICE_TIP } from '../InventoryDashboard/AiPriceCell';
import './UiKit.css';

// Sample content only: nothing on this page calls the API.
const NOW = Date.now();
const FRESH = describeCacheAge({ cached_at: new Date(NOW - 4 * 60000).toISOString() }, NOW);
const STALE = describeCacheAge({ cached_at: new Date(NOW - 3 * 3600000).toISOString() }, NOW);

const AGING = [
    { id: '0-30', label: '0–30 days', value: 212, tone: 'sage' },
    { id: '31-90', label: '31–90 days', value: 348, tone: 'slate' },
    { id: '91-180', label: '91–180 days', value: 301, tone: 'amber' },
    { id: '180+', label: '180+ days', value: 406, tone: 'red' },
];
const RISK = [
    { id: 'High', label: 'High', value: 118, tone: 'red' },
    { id: 'Medium', label: 'Medium', value: 327, tone: 'amber' },
    { id: 'Low', label: 'Low', value: 159, tone: 'sage' },
];
const pct = (n, total) => `${Math.round((n / total) * 100)}% of lot`;

const unit = (stock, name, cat, days, asking, ai, extra = {}) => ({
    id: stock, StockNumber: stock, name, cat, days, Best_Sell_Price: asking, PredictedPrice: ai, ...extra,
});

// One row per AI price state: in line / above / well above / below / Review / none.
const UNITS = [
    unit('121617', 'John Deere S790', 'Combines', 432, 492000, 470500),
    unit('118204', 'Case IH Magnum 340', 'Tractors 4WD', 118, 289000, 269000),
    unit('120988', 'Kinze 3660 Planter', 'Planters', 205, 138500, 119900),
    unit('97726', 'MacDon FD145', 'Draper heads', 45, 61000, 68000),
    unit('119412', 'Bobcat T770', 'Track loaders', 63, 74900, 52000, { PriceStatus: 'Review' }),
    unit('N24051', 'New Holland T7.270', 'Tractors 2WD', 12, 318000, null),
];

const agingTone = (d) => (d > 180 ? 'red' : d > 90 ? 'amber' : d > 30 ? 'slate' : 'sage');

const COLUMNS = [
    {
        key: 'dot', label: <span className="v2-sr">Aging</span>,
        render: (u) => <i className={`v2-age v2-tone-${agingTone(u.days)}`} aria-hidden="true" />,
    },
    { key: 'StockNumber', label: 'Stock #', sortable: true, render: (u) => <span className="v2-mono">{u.StockNumber}</span> },
    {
        key: 'name', label: 'Equipment', sortable: true,
        render: (u) => (
            <span className="v2-eq">
                <b>{u.name}</b>
                <span className="v2-eq-sub v2-mono">{u.cat}</span>
            </span>
        ),
    },
    { key: 'days', label: 'Days', sortable: true, align: 'right', defaultDirection: 'desc' },
    { key: 'Best_Sell_Price', label: 'Price', sortable: true, align: 'right', render: (u) => formatCurrency(u.Best_Sell_Price) },
    {
        key: 'ai', label: 'AI price', align: 'right',
        render: (u) => (
            <span className="v2-ai-cell">
                <span aria-hidden="true">{u.PredictedPrice ? formatCurrency(u.PredictedPrice) : ''}</span>
                <GapChip unit={u} />
            </span>
        ),
    },
    {
        key: 'act', label: <span className="v2-sr">Actions</span>,
        render: (u) => (
            <IconButton label={`Add a task for ${u.StockNumber}`} onClick={() => {}}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
            </IconButton>
        ),
    },
];

const sorters = {
    StockNumber: (a, b) => String(a.StockNumber).localeCompare(String(b.StockNumber)),
    name: (a, b) => a.name.localeCompare(b.name),
    days: (a, b) => a.days - b.days,
    Best_Sell_Price: (a, b) => a.Best_Sell_Price - b.Best_Sell_Price,
};

function Section({ id, title, note, children }) {
    return (
        <section className="kit-sec" aria-labelledby={id}>
            <div className="kit-sec-head">
                <h2 id={id}>{title}</h2>
                {note && <p>{note}</p>}
            </div>
            {children}
        </section>
    );
}

function Swatch({ name }) {
    return (
        <div className="kit-swatch">
            <span style={{ background: `var(--v2-${name})` }} />
            <code>--v2-{name}</code>
        </div>
    );
}

const COLOUR_TOKENS = [
    'ground', 'surface', 'tint', 'tint-hover', 'line', 'divider', 'mist', 'mist-line',
    'ink', 'ink-soft', 'label', 'sage', 'sage-deep', 'sage-soft', 'sage-select', 'hunter', 'slate',
    'amber', 'amber-soft', 'red', 'red-soft', 'teal', 'teal-soft', 'grey', 'grey-soft',
];

const TYPE_SCALE = [
    ['h1', 'Inventory', 'display'], ['h2', 'Needs attention', 'display'], ['title', 'All inventory', 'display'],
    ['h3', 'Used lot value', 'display'], ['base', 'Body copy, 14px Inter', 'body'], ['body', 'Table cells, 13px', 'body'],
    ['ctl', 'Buttons, chips and nav', 'body'], ['xs', 'Sub-lines and meta', 'body'], ['eyebrow', 'EYEBROW LABEL', 'body'],
];

// One sample of every surface, rendered under a forced palette
// (.v2-theme-light / .v2-theme-dark), so both can be reviewed side by side
// whatever the page's own theme is.
const PREVIEW_UNITS = UNITS.slice(0, 3);
const PREVIEW_COLUMNS = COLUMNS.filter((c) => ['dot', 'StockNumber', 'name', 'days', 'ai'].includes(c.key));

function ThemePreview({ theme }) {
    const name = theme === 'dark' ? 'Dark' : 'Light';
    return (
        <div className={`kit-theme-pane v2-theme-${theme}`} aria-label={`${name} theme preview`} role="group">
            <p className="v2-eyebrow">{name}</p>
            <div className="kit-theme-bar">
                <img src="/ironsight-wordmark.png" alt="" className="kit-theme-mark" />
                <span className="kit-theme-nav"><span aria-current="page">Inventory</span><span>Branches</span></span>
                <span className="v2-avatar" aria-hidden="true">D</span>
            </div>
            <div className="kit-row">
                <Button>Columns</Button>
                <Button variant="primary">Open unit</Button>
                <Button variant="danger">Delete</Button>
                <Button variant="link">Clear all</Button>
                <span className="v2-pill"><span className="v2-pill-k">Make</span> John Deere</span>
            </div>
            <div className="kit-row">
                {UNITS.map((u) => <GapChip key={u.id} unit={u} />)}
                <Chip tone="neutral">New</Chip>
            </div>
            <div className="v2-kpis kit-theme-kpis">
                <div className="v2-kpi">
                    <div className="v2-kpi-head"><span className="v2-kpi-title">Inventory aging</span></div>
                    <StackBar segments={AGING.map((b) => ({ key: b.id, tone: b.tone, value: b.value }))} />
                    <Buckets columns={2}>
                        <Bucket label="91–180 days" value="301" sub="24% of lot" tone="amber" />
                        <Bucket label="180+ days" value="406" sub="32% of lot" tone="red" pressed onClick={() => {}} />
                    </Buckets>
                </div>
            </div>
            <div className="v2-board">
                <DataTable
                    label={`Sample units, ${name.toLowerCase()} theme`}
                    columns={PREVIEW_COLUMNS}
                    rows={PREVIEW_UNITS}
                    getRowId={(u) => u.id}
                    selectedId={PREVIEW_UNITS[1].id}
                    onRowClick={() => {}}
                />
            </div>
            <div className="kit-row kit-theme-float">
                <div className="v2-toast"><span className="v2-toast-ok" aria-hidden="true">✓</span><span className="v2-toast-text">Task completed</span><button type="button" className="v2-toast-undo">Undo</button></div>
                <div className="v2-tip kit-theme-tip">Sum of AI estimated sale prices for the same units.</div>
            </div>
            <div className="kit-grid">
                <div className="v2-card"><ErrorState message="Failed to load inventory data." onRetry={() => {}} /></div>
                <div className="v2-card"><Skeleton lines={3} /></div>
            </div>
        </div>
    );
}

ThemePreview.propTypes = { theme: PropTypes.oneOf(['light', 'dark']).isRequired };

export default function UiKitPage() {
    const [log, setLog] = useState('Nothing yet. Use the components below.');
    const say = (text) => setLog(text);

    // KPI band
    const [aging, setAging] = useState(['180+']);
    const [risk, setRisk] = useState(null);
    const [gapSort, setGapSort] = useState(false);

    // Buttons
    const [type, setType] = useState('All');
    const [density, setDensity] = useState('comfy');

    // Pills
    const searchRef = useRef(null);
    const START_PILLS = [
        { id: 'loc', label: 'Location', value: 'Fargo' },
        { id: 'cat', label: 'Category', value: 'Tractors 4WD' },
        { id: 'make', label: 'Make', value: 'John Deere' },
        { id: 'aging', label: 'Aging', value: '180+ days' },
    ];
    const [pills, setPills] = useState(START_PILLS);

    // Overlays
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { toast, showToast, dismissToast } = useToast();

    // Tabs
    const [tab, setTab] = useState('ai');

    // Table
    const [sort, setSort] = useState({ key: 'days', direction: 'desc' });
    const [selected, setSelected] = useState(null);
    const [showEmpty, setShowEmpty] = useState(false);
    const rows = useMemo(() => {
        const list = [...UNITS];
        const cmp = sorters[sort.key];
        if (cmp) list.sort((a, b) => (sort.direction === 'asc' ? cmp(a, b) : cmp(b, a)));
        return list;
    }, [sort]);

    const total = AGING.reduce((n, b) => n + b.value, 0);
    const kitNav = (e, to) => { e.preventDefault(); say(`Nav link to ${to} (prevented on the kit page)`); };

    return (
        <div className="ui-v2 v2-kit">
            <div className="kit-head">
                <p className="v2-eyebrow">IronSight redesign · step 2</p>
                <h1>UI kit</h1>
                <p className="kit-lede">
                    Every shared v2 primitive in its states, with sample IronSight content. Nothing here talks to the
                    API.
                    {/* Only a rollback build (REACT_APP_ENABLE_V1=true) has the old design to go back to. */}
                    {isV1Enabled() && <> Turn the new design off again with <code>?ui=v1</code>.</>}
                </p>
                <p className="kit-log" role="log" aria-live="polite"><b>Last action:</b> {log}</p>
                <div className="kit-row">
                    <span className="kit-label">Page theme</span>
                    <ThemeToggle />
                    <span className="kit-cap-inline">Light, Dark or System; applies to the whole page.</span>
                </div>
            </div>

            <Section id="kit-tokens" title="Tokens" note="--v2-* custom properties in styles/variables.css, shown in the page theme. Each has a dark twin (D4).">
                <div className="kit-swatches">{COLOUR_TOKENS.map((t) => <Swatch key={t} name={t} />)}</div>
                <div className="kit-type">
                    {TYPE_SCALE.map(([size, text, fam]) => (
                        <div key={size} className="kit-type-row">
                            <code>--v2-fs-{size}</code>
                            <span style={{ fontSize: `var(--v2-fs-${size})`, fontFamily: `var(--v2-f-${fam})`, fontWeight: fam === 'display' ? 600 : 500 }}>{text}</span>
                        </div>
                    ))}
                    <div className="kit-type-row"><code>--v2-f-mono</code><span className="v2-mono">121617 · ROT · 2024</span></div>
                </div>
            </Section>

            <Section id="kit-themes" title="Light and dark" note="The same components under each palette, whatever the page theme. Contrast for both is checked in styles/v2Contrast.test.js.">
                <div className="kit-themes">
                    <ThemePreview theme="light" />
                    <ThemePreview theme="dark" />
                </div>
            </Section>

            <Section id="kit-topbar" title="Top bar" note="AppTopBar feeds this from the real app; here it gets sample props.">
                <div className="kit-frame">
                    <TopBar
                        dealership="Alpha Machinery"
                        cacheStatus={FRESH}
                        current="inventory"
                        onNavigate={kitNav}
                        showRefresh
                        onRefresh={() => say('Refresh (the app calls loadInventory(true))')}
                        userName="Dana Miller"
                        userEmail="dana@alpha-machinery.com"
                        onSignOut={() => say('Sign out (the app calls logout)')}
                    />
                    <p className="kit-cap">Dealer user, one dealership: no Switch, no Admin.</p>
                </div>
                <div className="kit-frame">
                    <TopBar
                        dealership="Gooseneck Implement"
                        readOnly
                        canSwitch
                        onSwitch={() => say('Switch dealership (the app opens "Choose a dealership")')}
                        cacheStatus={STALE}
                        current="branches"
                        onNavigate={kitNav}
                        isAdmin
                        userName="Lishan Soosaisanthar"
                        userEmail="lishan@fusable.com"
                        onSignOut={() => say('Sign out')}
                    />
                    <p className="kit-cap">Fusable staff on a customer: Read-only, Switch dealership, Admin, stale data in amber, Refresh only on the inventory page, as today.</p>
                </div>
                <div className="kit-frame">
                    <TopBar
                        dealership="Gooseneck Implement"
                        cacheStatus={FRESH}
                        current="inventory"
                        onNavigate={kitNav}
                        showRefresh
                        refreshing
                        onRefresh={() => {}}
                        userName="Dana Miller"
                        onSignOut={() => say('Sign out')}
                    />
                    <p className="kit-cap">Refresh in progress: spinner and aria-busy.</p>
                </div>
                <div className="kit-frame">
                    <TopBar
                        dealership="Fusable"
                        dealershipTitle="Organization currently in view"
                        scopeLabel="Fusable Admin"
                        current="admin"
                        onNavigate={kitNav}
                        isAdmin
                        userName="Lishan Soosaisanthar"
                        userEmail="lishan@fusable.com"
                        onSignOut={() => say('Sign out')}
                    />
                    <p className="kit-cap">Admin console: scope and org; nav links go through the dealership picker (D18).</p>
                </div>
            </Section>

            <Section id="kit-kpi" title="KPI band, buckets" note="Buckets are toggles (aria-pressed). Numbers are sample values.">
                <KpiBand label="Inventory summary">
                    <KpiSection
                        title="Inventory aging"
                        meta={<><b>{total.toLocaleString('en-US')}</b> units</>}
                        bar={<StackBar segments={AGING.map((b) => ({ key: b.id, tone: b.tone, value: b.value }))} />}
                    >
                        <Buckets columns={4}>
                            {AGING.map((b) => (
                                <Bucket
                                    key={b.id}
                                    label={b.label}
                                    value={b.value.toLocaleString('en-US')}
                                    sub={pct(b.value, total)}
                                    tone={b.tone}
                                    pressed={aging.includes(b.id)}
                                    onClick={() => setAging((a) => (a.includes(b.id) ? a.filter((x) => x !== b.id) : [...a, b.id]))}
                                />
                            ))}
                        </Buckets>
                    </KpiSection>
                    <KpiSection
                        title="Sell-speed risk"
                        meta={<><b>604</b> scored</>}
                        bar={<StackBar segments={RISK.map((b) => ({ key: b.id, tone: b.tone, value: b.value }))} />}
                    >
                        <Buckets columns={3}>
                            {RISK.map((b) => (
                                <Bucket
                                    key={b.id}
                                    label={b.label}
                                    value={b.value}
                                    sub={`${Math.round((b.value / 604) * 100)}%`}
                                    tone={b.tone}
                                    pressed={risk === b.id}
                                    onClick={() => setRisk((r) => (r === b.id ? null : b.id))}
                                />
                            ))}
                        </Buckets>
                    </KpiSection>
                    <KpiSection
                        title="Used lot value"
                        info={<Tooltip label="Coverage" content="Based on 588 of 604 used units with both an asking price and an AI price. New units have no AI price." />}
                        meta="588 of 604 priced"
                    >
                        <Buckets columns={3}>
                            <Tooltip content={LOT_VALUE_TIPS.asking}><Bucket label="Asking" value={formatMoneyShort(91.0e6)} sub="list prices" tabIndex={0} /></Tooltip>
                            <Tooltip content={LOT_VALUE_TIPS.ai}><Bucket label="AI value" value={formatMoneyShort(86.0e6)} sub="model estimate" tabIndex={0} /></Tooltip>
                            <Bucket
                                className="v2-bk--amber"
                                label="Gap"
                                value="+$5.0M"
                                sub="+5.9% above AI · sort by gap"
                                pressed={gapSort}
                                onClick={() => setGapSort((v) => !v)}
                                title="Sort the table by AI gap, most overpriced first"
                            />
                        </Buckets>
                    </KpiSection>
                </KpiBand>
            </Section>

            <Section id="kit-chips" title="Chips and GapChip" note="GapChip reuses utils/aiPrice.js and the GAP_THRESHOLDS scale in utils/lotValue.js; it has no thresholds of its own.">
                <div className="kit-row">
                    {UNITS.map((u) => (
                        <div key={u.id} className="kit-chipcase">
                            <GapChip unit={u} />
                            <span>{u.PriceStatus === 'Review' ? 'Review' : u.PredictedPrice ? `${formatMoneyShort(u.Best_Sell_Price)} vs ${formatMoneyShort(u.PredictedPrice)}` : 'no AI price'}</span>
                        </div>
                    ))}
                </div>
                <div className="kit-row">
                    <span className="kit-label">Aggregate gap</span>
                    {[-0.084, 0.012, 0.059, 0.142, null].map((g) => <GapChip key={String(g)} gap={g} />)}
                </div>
                <div className="kit-row">
                    <span className="kit-label">Tones</span>
                    <Chip tone="ok">In line</Chip>
                    <Chip tone="warn">Watch list</Chip>
                    <Chip tone="crit">Unit issues</Chip>
                    <Chip tone="below">Below AI</Chip>
                    <Chip tone="teal">Live quote</Chip>
                    <Chip tone="review">Review</Chip>
                    <Chip tone="neutral">New</Chip>
                </div>
            </Section>

            <Section id="kit-pills" title="Pills" note="Remove a pill: focus moves to the next one, else the previous, else back to Search.">
                <div className="kit-row">
                    <label className="v2-search">
                        <span className="v2-sr">Search</span>
                        <input ref={searchRef} type="search" placeholder="Search stock #, make, model" />
                    </label>
                    <Button variant="ghost" size="sm" onClick={() => setPills(START_PILLS)}>Reset pills</Button>
                </div>
                <PillList
                    label="Active filters"
                    items={pills}
                    onRemove={(id) => { setPills((p) => p.filter((x) => x.id !== id)); say(`Removed filter ${id}`); }}
                    onClearAll={() => { setPills([]); say('Cleared all filters'); }}
                    fallbackFocusRef={searchRef}
                />
            </Section>

            <Section id="kit-buttons" title="Buttons, icon buttons, segmented control">
                <div className="kit-row">
                    <Button onClick={() => say('Default button')}>Columns</Button>
                    <Button variant="primary" onClick={() => say('Primary button')}>Open unit</Button>
                    <Button variant="ghost">Ghost</Button>
                    <Button variant="danger">Delete</Button>
                    <Button variant="link">Clear all filters</Button>
                    <Button size="sm">Small</Button>
                    <Button disabled>Disabled</Button>
                    <IconButton label="Close preview" onClick={() => say('Icon button')}><CloseIcon /></IconButton>
                </div>
                <div className="kit-row">
                    <SegmentedControl
                        label="New or used"
                        options={[{ value: 'All', label: 'All' }, { value: 'N', label: 'New' }, { value: 'U', label: 'Used' }]}
                        value={type}
                        onChange={(v) => { setType(v); say(`Type: ${v}`); }}
                    />
                    <SegmentedControl
                        label="Row density"
                        options={[{ value: 'comfy', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }]}
                        value={density}
                        onChange={setDensity}
                    />
                </div>
            </Section>

            <Section id="kit-tips" title="Tooltip" note="Hover, keyboard focus or tap. Escape closes. role=tooltip + aria-describedby.">
                <div className="kit-row">
                    <span className="kit-inline">AI price <Tooltip label="About AI Price" content={AI_PRICE_TIP} /></span>
                    <Tooltip content="Sort the table by AI gap, most overpriced first">
                        <Button>Gap</Button>
                    </Tooltip>
                </div>
            </Section>

            <Section id="kit-pop" title="Popover" note="Focus moves in; Escape returns it to the button; an outside click closes.">
                <div className="kit-row">
                    <Popover triggerLabel="Filters" title="More filters" badge={2} width={300}>
                        <label className="v2-fld"><span>Sell speed</span>
                            <select className="v2-sel"><option>All speeds</option><option>Quick Sell</option><option>Normal</option><option>Slow</option><option>Critical</option></select>
                        </label>
                        <label className="v2-fld"><span>Interest</span>
                            <select className="v2-sel"><option>All interest</option><option>Hot</option><option>Warm</option><option>Cold</option><option>No Interest</option></select>
                        </label>
                    </Popover>
                    <Popover triggerLabel="Columns" title="Columns" align="left" width={280}>
                        <h3 className="v2-pop-title">Columns</h3>
                        <SegmentedControl
                            label="Row density"
                            options={[{ value: 'comfy', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }]}
                            value={density}
                            onChange={setDensity}
                        />
                    </Popover>
                </div>
            </Section>

            <Section id="kit-overlays" title="Drawer, Dialog, ConfirmDialog" note="Focus trap, Escape, scrim click, focus returns to the opener. Confirm ignores clicks within 400 ms of opening.">
                <div className="kit-row">
                    <Button onClick={() => setDrawerOpen(true)}>Open user drawer</Button>
                    <Button onClick={() => setDialogOpen(true)}>Open dialog</Button>
                    <Button variant="danger" onClick={() => setConfirmOpen(true)}>Delete dealership…</Button>
                </div>
                <Drawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    title="Dana Miller"
                    subtitle="dana@alpha-machinery.com"
                    footer={<><Button onClick={() => setDrawerOpen(false)}>Cancel</Button><Button variant="primary" onClick={() => { setDrawerOpen(false); say('Saved user'); }}>Save</Button></>}
                >
                    <label className="v2-fld"><span>Role</span>
                        <select className="v2-sel"><option>User</option><option>Dealer admin</option></select>
                    </label>
                    <label className="v2-fld"><span>Display name</span><input className="v2-input" defaultValue="Dana Miller" /></label>
                </Drawer>
                <Dialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    title="Edit dealership"
                    footer={<><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="primary" onClick={() => { setDialogOpen(false); say('Saved dealership'); }}>Save changes</Button></>}
                >
                    <label className="v2-fld"><span>Name</span><input className="v2-input" defaultValue="Alpha Machinery" /></label>
                </Dialog>
                <ConfirmDialog
                    open={confirmOpen}
                    title="Delete “Alpha Machinery”?"
                    message="This removes the dealer from IronSight. Its sub-organizations and users must be moved or removed first."
                    confirmLabel="Delete"
                    cancelLabel="Keep it"
                    danger
                    onConfirm={() => { setConfirmOpen(false); say('Confirmed delete'); }}
                    onCancel={() => setConfirmOpen(false)}
                />
            </Section>

            <Section id="kit-toast" title="Toast" note="role=status, never takes focus, times out after 6 s; with Undo a bar drains.">
                <div className="kit-row">
                    <Button onClick={() => showToast({ text: 'Data refreshed' })}>Show toast</Button>
                    <Button onClick={() => showToast({ text: 'Task completed', onUndo: () => say('Undo: task reopened') })}>Show toast with Undo</Button>
                </div>
                <ToastRegion toast={toast} onDismiss={dismissToast} />
            </Section>

            <Section id="kit-tabs" title="Tabs" note="Arrow keys, Home and End; only the selected tab is in the Tab order.">
                <Tabs
                    label="Unit sections"
                    value={tab}
                    onChange={setTab}
                    tabs={[
                        { id: 'ai', label: 'Intelligence', panel: <Skeleton lines={3} /> },
                        { id: 'overview', label: 'Overview', panel: <p>Unit facts and photos.</p> },
                        { id: 'pricing', label: 'Pricing & Usage', panel: <p>Price history and carrying cost.</p> },
                        { id: 'comps', label: 'Comps', count: 12, panel: <p>12 market comparables.</p> },
                        { id: 'activity', label: 'Activity', count: 3, panel: <p>3 open tasks.</p> },
                    ]}
                />
            </Section>

            <Section id="kit-table" title="DataTable" note="Own scroll container, sticky header, aria-sort, rows operable with Enter / Space.">
                <div className="kit-row">
                    <Button size="sm" onClick={() => setShowEmpty((v) => !v)}>{showEmpty ? 'Show rows' : 'Show empty state'}</Button>
                </div>
                <div className="v2-board">
                    <DataTable
                        label="Inventory units (sample)"
                        columns={COLUMNS}
                        rows={showEmpty ? [] : rows}
                        getRowId={(u) => u.id}
                        sort={sort}
                        onSortChange={setSort}
                        selectedId={selected}
                        onRowClick={(u) => { setSelected(u.id); say(`Row ${u.StockNumber} (the app opens the preview)`); }}
                        compact={density === 'compact'}
                        maxHeight={360}
                        empty={(
                            <EmptyState
                                title="No units match these filters"
                                action={<Button onClick={() => setShowEmpty(false)}>Clear all filters</Button>}
                            />
                        )}
                    />
                </div>
            </Section>

            <Section id="kit-states" title="Empty, error, loading, skeleton">
                <div className="kit-grid">
                    <div className="v2-card"><EmptyState title="No open tasks on this lot" message="Tasks you add from a unit show up here." /></div>
                    <div className="v2-card"><ErrorState message="Failed to load inventory data. Please try again." onRetry={() => say('Retry')} /></div>
                    <div className="v2-card"><LoadingState label="Loading inventory data..." /></div>
                    <div className="v2-card"><Skeleton lines={4} /></div>
                </div>
            </Section>
        </div>
    );
}
